/**
 * @fileoverview The test block — an experiment's written prediction and, fused
 * into the same plate, the lines that say how you would know and why it is
 * worth knowing.
 *
 * An experiment is a claim plus a way to check it. The claim is `prediction`
 * ("what the old pattern says will happen"); `whatToNotice` is the measurement
 * spec FOR that claim, not a separate instruction; `rationale` is what rides on
 * the answer. All three are beats of one reading, so they render as one warm
 * plate — the claim, then a quiet "what to watch for" line, then "why this
 * matters" — each later beat separated by the hairline that keeps them one
 * block rather than several stacked.
 *
 * The block surfaces on three surfaces — the in-chat proposal card, the in-chat
 * practice-reference panel, and the /practices detail page — so the labels are
 * authored once here. Only the proposal card passes
 * `rationale`; the other two carry their own rationale callout, so the beat is
 * opt-in and their output is unchanged.
 *
 * Every beat is a soft slot (orientation practices, prediction-less blueprint
 * codes, and pre-cutover snapshots carry no prediction). Whichever beat comes
 * first takes the plate's eyebrow slot and the rest follow as sub-beats, so a
 * missing beat is never rendered as an empty callout:
 *   prediction + notice → the claim, then the watch line under its sub-label
 *   prediction only     → the claim alone
 *   notice only         → the notice text under a plain watch-for label, never
 *                         an empty prediction slot
 *   + rationale         → appended as a final "why this matters" sub-beat, or
 *                         promoted to the eyebrow when it is the only beat
 *   none of the three   → `null`; callers append nothing
 *
 * @module components/predictionBlock
 */

import { buildTakeawayBox } from './takeawayBox.js';

/**
 * Label shown above the prediction. Pinned to the help card's specimen row
 * (`help-content/index.js`) so the user meets one name for this block.
 * @type {string}
 */
export const PREDICTION_BLOCK_LABEL = "What We're Testing";

/**
 * Past-tense label for a closed experiment. Same block, same voice — only the
 * tense moves, because on a Finding the window has already run.
 * @type {string}
 */
export const PREDICTION_BLOCK_LABEL_PAST = 'What We Tested';

/**
 * Sub-label for the fused watch line. One register-consistent phrasing across
 * all three surfaces; it doubles as the block's main label when a row carries
 * a notice but no prediction.
 * @type {string}
 */
export const NOTICE_SUBLABEL = 'What To Watch For';

/**
 * Past-tense sub-label, for a Finding's closed window.
 * @type {string}
 */
export const NOTICE_SUBLABEL_PAST = 'What We Watched For';

/**
 * Label for the rationale beat — what rides on the answer. Same wording the
 * /experiments detail page and the practice-reference panel give their own
 * standalone rationale callouts, so the name is one name everywhere.
 * @type {string}
 */
export const RATIONALE_SUBLABEL = 'Why This Matters';

/**
 * What reality does with the claim, as one sentence per verdict enum
 * (`CHECKIN_PREDICTION_VERDICTS`, backend `TreatmentStrategy`). Authored here
 * beside the prediction labels because a verdict is only ever read as the
 * answer to a prediction — the in-chat takeaway card leads with it and the
 * /experiments finding plate closes on it, and the two must never word the
 * same finding differently.
 *
 * 🚨 THE SUBJECT AND THE POLARITY ARE BOTH LOAD-BEARING. `prediction` does not
 * hold a forecast the app made; it holds THE CLAIM ON TRIAL — what the old
 * pattern says will happen, which the experiment exists to knock down.
 * `compose-practice.md` derives it from "what this blueprint is designed to
 * disconfirm", `actionTools.js` glosses `didnt_happen` as "the feared thing did
 * not occur", and `practiceBlueprints.js` calls a wrong prediction "the
 * disconfirming evidence the belief needs to update". So `didnt_happen` is the
 * RESULT THE FEATURE IS BUILT TO PRODUCE, not a miss.
 *
 * The subject slot therefore holds THE TESTED CLAIM — "What we tested", the
 * label noun already printed above the claim on every surface, in the same
 * joint "we" the rest of the card speaks. Two subjects it must never take.
 * Not "the prediction": a prediction that "didn't happen" reads as the app
 * guessing wrong on the one outcome that is the win. And not "the belief" or
 * "your worry": the claim is agent-authored, so naming it as the user's scores
 * them for words they never wrote. The verb stays "hold up", which is what
 * makes `didnt_happen` read as progress rather than as a failure.
 *
 * `happened` carries "this time" for the same reason: without it, a claim that
 * survives reads as a verdict on the user's life rather than one window's
 * data. The prompt agrees — one disconfirmation "is a start, not the end", and
 * a claim like this updates over repeated reps.
 *
 * The fear frame stays avoided: these claims generalize across fears, pattern-
 * claims, capability-beliefs and stories-about-us, so nothing here says
 * "fear".
 * @type {Object<string, string>}
 */
export const VERDICT_BANDS = {
  didnt_happen: "What we tested didn't hold up.",
  partly: 'What we tested only partly held up.',
  happened: 'What we tested held up this time.',
  unclear: 'Too little signal to tell.'
};

/**
 * Band for a closed experiment whose report recorded no verdict — pre-cutover
 * completions carry none and still closed with a finding. A missing verdict is
 * its own band, never an empty plate.
 * @type {string}
 */
export const VERDICT_BAND_FALLBACK = 'Closed with a finding.';

/** @param {*} value @returns {string} trimmed string, or '' for anything else */
const clean = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * Append one sub-beat — the hairline sub-label plus its text — to a plate.
 * The hairline (on `.sd-takeaway__sublabel`) is what makes the result one
 * plate with several beats instead of several plates jammed together.
 *
 * @param {HTMLElement} box
 * @param {{label: string, text: string}} beat
 */
function appendBeat(box, { label, text }) {
  const subLabel = document.createElement('div');
  subLabel.className = 'sd-takeaway__sublabel';
  subLabel.textContent = label;
  box.appendChild(subLabel);

  const subText = document.createElement('p');
  subText.className = 'sd-takeaway__text sd-takeaway__text--sub';
  subText.textContent = text;
  box.appendChild(subText);
}

/**
 * Build the test block.
 *
 * @param {string} [predictionText] - The prediction; empty/absent is allowed
 *   (see the null matrix above)
 * @param {Object} [options]
 * @param {boolean} [options.past=false] - Use the closed-experiment labels
 * @param {string} [options.notice] - The `whatToNotice` measurement line
 * @param {string} [options.rationale] - The "why this matters" line. Opt-in:
 *   callers that render their own rationale callout simply omit it
 * @returns {HTMLElement|null} `.sd-takeaway` element, or null when the row
 *   carries none of the three beats
 */
export function buildPredictionBlock(
  predictionText, { past = false, notice = '', rationale = '' } = {}
) {
  const prediction = clean(predictionText);
  const watchFor = clean(notice);
  const why = clean(rationale);
  if (!prediction && !watchFor && !why) return null;

  // Beats in reading order: the claim, how you would know, what rides on it.
  const beats = [
    // Unquoted: the label ("What We're Testing") is what marks this line as the
    // claim under test, so punctuation adding the same signal only reads as
    // someone being quoted. Nothing model-authored is quoted on this feature.
    prediction && {
      label: past ? PREDICTION_BLOCK_LABEL_PAST : PREDICTION_BLOCK_LABEL,
      text: prediction
    },
    watchFor && { label: past ? NOTICE_SUBLABEL_PAST : NOTICE_SUBLABEL, text: watchFor },
    why && { label: RATIONALE_SUBLABEL, text: why }
  ].filter(Boolean);

  // The first beat present takes the eyebrow slot — so a row with no prediction
  // leads on its watch line rather than on an empty claim slot, which would
  // read as a missing value instead of an absent one.
  const [lead, ...rest] = beats;

  const box = buildTakeawayBox({
    label: lead.label,
    text: lead.text,
    // `--test` marks the fused block wherever it lands; `--prediction` is added
    // only when a claim is actually present, so "no prediction on this row"
    // stays answerable from the DOM.
    modifier: prediction ? 'sd-takeaway--test sd-takeaway--prediction' : 'sd-takeaway--test'
  });

  for (const beat of rest) appendBeat(box, beat);

  return box;
}
